/**
 * Firebase Authentication Service
 * Handles all Firebase authentication operations
 */

import {
  signInWithCredential,
  signInAnonymously,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
// Removed unused import: getFunctions, httpsCallable (no longer calling Cloud Function at login)
import bcrypt from 'bcryptjs';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '../config/firebase';
import { sessionTimeoutService } from './sessionTimeoutService';
import { googleDriveService } from './googleDriveService';
import { deletionRiskService } from './deletionRiskService';
import { encryptionService } from './encryptionService';
import type {
  GoogleAuthResponse,
  FamilyAuthResponse,
  PilotSignupRequest,
  PilotSignupResponse
} from '../types/auth.types';

class FirebaseAuthService {
  /**
   * Reset inactivity tracking when user logs in
   * This calls the Cloud Function to reset the check-in reminder system
   * NOTE: We only update the pilot's lastActivityAt timestamp here.
   * The Cloud Function resetInactivityTracking requires a profileId which we don't have at login time.
   * Profile-level inactivity tracking will be reset when profiles are loaded.
   */
  private async resetUserInactivityTracking(userId: string): Promise<void> {
    try {
      // Update lastActivityAt on the pilot document
      const pilotRef = doc(db, 'pilots', userId);
      await updateDoc(pilotRef, {
        lastActivityAt: serverTimestamp()
      });

      // Note: Not calling the Cloud Function here because it requires profileId
      // which we don't have at login time. The inactivity tracking will be reset
      // when the user's profiles are loaded and accessed.
    } catch (error) {
      // Non-critical - log but don't fail authentication
      console.warn('Failed to update pilot lastActivityAt:', error);
    }
  }

  /**
   * Compare family code with stored code (supports both hashed v2 and plaintext v1)
   * @param inputCode - The code entered by the user
   * @param storedCode - The code stored in the database
   * @param codeVersion - Version of the stored code (2 = bcrypt, 1 or undefined = plaintext)
   */
  private async compareFamilyCode(
    inputCode: string,
    storedCode: string | undefined,
    codeVersion?: number
  ): Promise<boolean> {
    if (!storedCode || !inputCode) return false;

    // Version 2: bcrypt hashed
    if (codeVersion === 2) {
      try {
        return await bcrypt.compare(inputCode, storedCode);
      } catch {
        return false;
      }
    }

    // Version 1 or undefined: plaintext comparison (backward compatibility)
    return storedCode === inputCode;
  }

  /**
   * Authenticate Pilot using Google OAuth
   */
  async authenticateWithGoogle(googleToken: string): Promise<GoogleAuthResponse> {
    try {
      // Create credential from Google token and sign in.
      // signInWithCredential replaces any existing session atomically — do NOT call
      // firebaseSignOut() first, as that fires onAuthStateChanged(null) which races
      // with the new sign-in and can trigger stale-session cleanup in ProfileContext.
      const credential = GoogleAuthProvider.credential(googleToken, null);
      const userCredential = await signInWithCredential(auth, credential);
      const user = userCredential.user;

      // Verify auth state is ready before Firestore operations
      // This ensures the auth token is properly set for Firestore rules
      if (!auth.currentUser) {
        throw new Error('Authentication state not ready');
      }

      // Force refresh the auth token to ensure Firestore has the latest auth state
      await user.getIdToken(true);

      // Check if pilot exists in Firestore
      const pilotRef = doc(db, 'pilots', user.uid);

      let pilotDoc;
      try {
        pilotDoc = await getDoc(pilotRef);
      } catch (firestoreError: any) {
        // Permission denied likely means Firestore rules are not properly deployed
        // Log the error but don't treat as "new user" - existing users would be locked out
        console.error('Firestore permission error reading pilot doc:', firestoreError);
        console.error('This usually means Firestore rules need to be deployed to Firebase');
        return {
          success: false,
          message: 'Authentication error. Please try again or contact support if the issue persists.'
        };
      }

      if (pilotDoc.exists()) {
        // Existing pilot - update lastSignIn and return profile data
        const pilotData = pilotDoc.data();

        // Update lastSignIn and lastActivityAt timestamp
        await updateDoc(pilotRef, {
          lastSignIn: serverTimestamp(),
          lastActivityAt: serverTimestamp()
        });

        // Record auth time for deletion risk assessment
        deletionRiskService.recordAuthTime();

        // Reset inactivity tracking (for check-in reminders)
        this.resetUserInactivityTracking(user.uid);

        return {
          success: true,
          sessionToken: await user.getIdToken(),
          userId: user.uid,
          userType: 'pilot',
          profile: {
            firstName: pilotData.firstName,
            lastName: pilotData.lastName,
            email: pilotData.email,
            photoURL: pilotData.photoURL || user.photoURL || '',
          }
        };
      } else {
        // New user - requires signup
        return {
          success: false,
          requiresSignup: true,
          message: 'Please complete signup to create your account'
        };
      }
    } catch (error: any) {
      console.error('Firebase auth error:', error);
      return {
        success: false,
        message: error.message || 'Failed to authenticate with Google'
      };
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // v2: authenticateWithMasterPin() REMOVED
  // Google Sign-in is the ONLY auth method in v2
  // ═══════════════════════════════════════════════════════════════

  /**
   * Authenticate Family Member (Co-Pilot or Passenger)
   * Requires email, pilot DOB, and family code for triple validation
   */
  async authenticateFamily(
    email: string,
    pilotDateOfBirth: string,
    familyCode: string
  ): Promise<FamilyAuthResponse> {
    try {
      // Sign in anonymously first to be able to query Firestore
      // Security rules require authentication even for reading pilots collection
      let wasAnonymous = false;
      if (!auth.currentUser) {
        try {
          await signInAnonymously(auth);
          wasAnonymous = true;
        } catch (anonError) {
          console.error('Anonymous auth failed:', anonError);
          return {
            success: false,
            message: 'Authentication service unavailable. Please try Google sign-in instead.'
          };
        }
      }

      // Query Firestore for pilot with matching date of birth
      // Use hash lookup for encrypted DOB field
      const pilotsRef = collection(db, 'pilots');
      let querySnapshot;
      try {
        // Hash the input DOB for lookup (matches encrypted storage)
        const inputDobHash = await encryptionService.hash(pilotDateOfBirth);
        const q = query(pilotsRef, where('dateOfBirthHash', '==', inputDobHash));
        querySnapshot = await getDocs(q);
      } catch (queryError: any) {
        console.error('Pilot query failed:', queryError);
        // Sign out anonymous user on failure
        if (wasAnonymous) {
          await firebaseSignOut(auth);
        }
        return {
          success: false,
          message: 'Unable to verify credentials. Please ask the Pilot (profile owner) to sign in once, then try again.'
        };
      }

      if (querySnapshot.empty) {
        return {
          success: false,
          message: 'User not found in LucidDock'
        };
      }

      // Get the first matching pilot
      const pilotDoc = querySnapshot.docs[0];
      const pilotData = pilotDoc.data();
      const pilotId = pilotDoc.id;

      // Get pilot's profile to check family code in invitations
      const profilesRef = collection(db, 'profiles');
      const profileQuery = query(profilesRef, where('ownerId', '==', pilotId));
      const profileSnapshot = await getDocs(profileQuery);

      if (profileSnapshot.empty) {
        return {
          success: false,
          message: 'Profile not found'
        };
      }

      // Get the first profile (pilot should have one profile)
      const profileDoc = profileSnapshot.docs[0];
      const profileData = profileDoc.data();

      // First, check if user is an active member (members array is source of truth for active users)
      const activeMember = profileData.members?.find((member: any) =>
        member.email?.toLowerCase() === email.toLowerCase() &&
        member.isActive === true
      );

      // Also look for matching invitation (for additional data or fallback)
      let matchingInvitation: any = null;
      if (activeMember) {
        // Try to find corresponding invitation with the family code
        matchingInvitation = profileData.invitations?.find((inv: any) =>
          inv.email?.toLowerCase() === email.toLowerCase() &&
          inv.inviteCode === familyCode
        );
      } else {
        // Not an active member - check invitations array
        matchingInvitation = profileData.invitations?.find((inv: any) =>
          inv.email?.toLowerCase() === email.toLowerCase() &&
          inv.inviteCode === familyCode &&
          inv.status === 'accepted'
        );
      }

      // If user is an active member, verify the family code matches
      if (activeMember) {
        // Verify family code matches their invitation, stored family code, or familyCode field
        // Check multiple possible locations where the code might be stored
        // Note: inviteCode in invitations is always plaintext, but member.familyCode may be hashed (v2)
        let codeMatches = matchingInvitation?.inviteCode === familyCode ||
          activeMember.inviteCode === familyCode;

        // Check member's familyCode (may be hashed with bcrypt for v2)
        if (!codeMatches && activeMember.familyCode) {
          codeMatches = await this.compareFamilyCode(
            familyCode,
            activeMember.familyCode,
            activeMember.familyCodeVersion
          );
        }

        // If no code matches, also check if there's any invitation for this email with the code
        if (!codeMatches) {
          const anyMatchingInvitation = profileData.invitations?.find((inv: any) =>
            inv.email?.toLowerCase() === email.toLowerCase() &&
            inv.inviteCode === familyCode
          );

          if (!anyMatchingInvitation) {
            return {
              success: false,
              message: 'Invalid family code. Please check your family code and try again.'
            };
          }
        }
      }

      if (!activeMember && !matchingInvitation) {
        // Check if there's an invitation with the family code but different email or status
        const invitationWithCode = profileData.invitations?.find((inv: any) => inv.inviteCode === familyCode);

        if (invitationWithCode) {
          if (invitationWithCode.status === 'revoked') {
            return {
              success: false,
              message: 'Your access has been revoked. Please contact the pilot to be re-invited.'
            };
          }

          if (invitationWithCode.status !== 'accepted') {
            return {
              success: false,
              message: 'This invitation has not been accepted yet. Please accept the invitation first.'
            };
          }

          if (invitationWithCode.email?.toLowerCase() !== email.toLowerCase()) {
            return {
              success: false,
              message: `Email mismatch. Please sign in with the email used when accepting the invitation: ${invitationWithCode.email}`
            };
          }
        }

        // Track failed attempts (current count before incrementing)
        const currentFailedAttempts = pilotData.failedLoginAttempts || 0;
        const lockoutUntil = pilotData.lockoutUntil?.toDate();

        // Check if account is locked
        if (lockoutUntil && lockoutUntil > new Date()) {
          const remainingMinutes = Math.ceil((lockoutUntil.getTime() - Date.now()) / 60000);
          return {
            success: false,
            message: `Account locked. Please try again in ${remainingMinutes} minutes.`
          };
        }

        // Increment failed attempts
        const newFailedAttempts = currentFailedAttempts + 1;

        // Update failed attempts
        await updateDoc(doc(db, 'pilots', pilotDoc.id), {
          failedLoginAttempts: newFailedAttempts,
          lastFailedAttempt: serverTimestamp()
        });

        // Check if should lock account (3rd failed attempt)
        if (newFailedAttempts >= 3) {
          const lockoutTime = new Date(Date.now() + 15 * 60 * 1000); // 15 minutes
          await updateDoc(doc(db, 'pilots', pilotDoc.id), {
            lockoutUntil: lockoutTime,
            failedLoginAttempts: 0
          });
          return {
            success: false,
            message: 'Too many failed attempts. Account locked for 15 minutes.'
          };
        } else if (newFailedAttempts === 2) {
          // 2nd failed attempt - show warning
          return {
            success: false,
            message: 'This is your last attempt. After this, your account will be locked for 15 minutes.'
          };
        } else {
          // 1st failed attempt - regular error
          return {
            success: false,
            message: 'Invalid credentials. Please check your email, date of birth, and family code.'
          };
        }
      }

      // Successful authentication - reset failed attempts
      await updateDoc(doc(db, 'pilots', pilotDoc.id), {
        failedLoginAttempts: 0,
        lockoutUntil: null,
        lastSuccessfulLogin: serverTimestamp()
      });

      // Import ProfileService here to avoid circular dependency
      const { ProfileService } = await import('./profileService');

      // Determine the member's userId and profile
      let memberProfileId: string;
      let memberUserId: string;

      if (activeMember) {
        // Active member - use their existing userId directly
        memberUserId = activeMember.userId;

        // Try to find their existing profile by querying Firestore directly
        const memberProfilesRef = collection(db, 'profiles');
        const memberProfileQuery = query(memberProfilesRef, where('ownerId', '==', memberUserId));
        const memberProfileSnapshot = await getDocs(memberProfileQuery);

        if (!memberProfileSnapshot.empty) {
          memberProfileId = memberProfileSnapshot.docs[0].id;
        } else if (matchingInvitation) {
          // Fallback: create profile from invitation if it exists
          memberProfileId = await ProfileService.addFamilyMemberFromInvitation(
            profileDoc.id,
            matchingInvitation.id
          );
        } else {
          // Create a minimal profile for the active member
          memberProfileId = await ProfileService.createProfileForFamilyMember(
            profileDoc.id,
            activeMember
          );
        }
      } else if (matchingInvitation) {
        // Found via invitation (not yet in members array as active)
        memberProfileId = await ProfileService.addFamilyMemberFromInvitation(
          profileDoc.id,
          matchingInvitation.id
        );
        const memberProfile = await ProfileService.loadProfile(memberProfileId);
        if (!memberProfile) {
          return {
            success: false,
            message: 'Failed to create member profile. Please try again.'
          };
        }
        memberUserId = memberProfile.ownerId;
      } else {
        // This shouldn't happen, but handle it gracefully
        return {
          success: false,
          message: 'Unable to authenticate. Please try again.'
        };
      }

      // Create a custom token for family member (using pilot's ID as base)
      // Note: Family members don't have their own Firebase Auth accounts
      const sessionToken = `family_${pilotDoc.id}_${Date.now()}`;

      // Get display name from active member or invitation
      const displayName = activeMember?.displayName || matchingInvitation?.displayName || '';
      const lastName = activeMember?.lastName || matchingInvitation?.lastName || '';

      return {
        success: true,
        sessionToken,
        userId: memberUserId,
        userType: 'family',
        profile: {
          firstName: displayName,
          lastName: lastName,
          email: email  // Use the email they signed in with
        }
      };
    } catch (error: any) {
      console.error('Family auth error:', error);
      // Provide more specific error message if available
      const errorMessage = error?.message || error?.code || 'Failed to authenticate family member';
      return {
        success: false,
        message: `Authentication failed: ${errorMessage}`
      };
    }
  }

  /**
   * Authenticate Family Member with Google OAuth
   * Email must be pre-authorized by pilot
   */
  async authenticateFamilyGoogle(googleToken: string): Promise<GoogleAuthResponse> {
    try {
      // 1. First, decode the Google token to get the picture URL directly
      // This is more reliable than user.photoURL from Firebase
      let pictureFromToken: string | undefined;
      try {
        const parts = googleToken.split('.');
        if (parts.length === 3) {
          const payload = parts[1];
          const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
          const tokenData = JSON.parse(decodedPayload);
          pictureFromToken = tokenData.picture;
        }
      } catch (decodeError) {
        // Silent fail - continue without picture
      }

      // 2. Sign in with Firebase to verify the token
      const credential = GoogleAuthProvider.credential(googleToken, null);
      const userCredential = await signInWithCredential(auth, credential);
      const user = userCredential.user;

      if (!user.email) {
        return {
          success: false,
          message: 'No email associated with Google account'
        };
      }

      // 2. Query profiles where user is a member using denormalized arrays
      // This respects Firestore security rules (only query what user can access)
      const profilesRef = collection(db, 'profiles');

      let foundInvitation: any = null;
      let foundMember: any = null;
      let foundProfile: any = null;

      // Check if user is also a Pilot (they can have both - Pilot on their own profile AND family member on another)
      const pilotRef = doc(db, 'pilots', user.uid);
      const pilotDoc = await getDoc(pilotRef);
      const isAlsoPilot = pilotDoc.exists();

      // Try to find profiles where user is in memberUserIds (all members - backward compat)
      try {
        const memberQuery = query(profilesRef, where('memberUserIds', 'array-contains', user.uid));
        const memberSnapshot = await getDocs(memberQuery);

        for (const profileDoc of memberSnapshot.docs) {
          const profileData = profileDoc.data();
          const activeMember = profileData.members?.find((member: any) =>
            member.userId === user.uid && member.isActive === true
          );
          if (activeMember) {
            foundMember = activeMember;
            foundProfile = { id: profileDoc.id, ...profileData };
            break;
          }
        }
      } catch (queryError) {
        // Query failed - likely no index or permission issue, continue to next method
        console.warn('memberUserIds query failed, trying alternative methods:', queryError);
      }

      // If not found via memberUserIds, try editorUserIds (Co-Pilot)
      if (!foundMember) {
        try {
          const editorQuery = query(profilesRef, where('editorUserIds', 'array-contains', user.uid));
          const editorSnapshot = await getDocs(editorQuery);

          for (const profileDoc of editorSnapshot.docs) {
            const profileData = profileDoc.data();
            const activeMember = profileData.members?.find((member: any) =>
              member.userId === user.uid && member.isActive === true
            );
            if (activeMember) {
              foundMember = activeMember;
              foundProfile = { id: profileDoc.id, ...profileData };
              break;
            }
          }
        } catch (queryError) {
          console.warn('editorUserIds query failed:', queryError);
        }
      }

      // If not found as editor, try viewerUserIds (Passenger)
      if (!foundMember) {
        try {
          const viewerQuery = query(profilesRef, where('viewerUserIds', 'array-contains', user.uid));
          const viewerSnapshot = await getDocs(viewerQuery);

          for (const profileDoc of viewerSnapshot.docs) {
            const profileData = profileDoc.data();
            const activeMember = profileData.members?.find((member: any) =>
              member.userId === user.uid && member.isActive === true
            );
            if (activeMember) {
              foundMember = activeMember;
              foundProfile = { id: profileDoc.id, ...profileData };
              break;
            }
          }
        } catch (queryError) {
          console.warn('viewerUserIds query failed:', queryError);
        }
      }

      // NEW: Try to find profile by memberEmails (for first-time login before userId sync)
      // This is the key fix for new Co-Pilots - their email is in memberEmails but userId isn't synced yet
      if (!foundMember && user.email) {
        try {
          const emailQuery = query(profilesRef, where('memberEmails', 'array-contains', user.email.toLowerCase()));
          const emailSnapshot = await getDocs(emailQuery);

          for (const profileDoc of emailSnapshot.docs) {
            const profileData = profileDoc.data();
            // Find member by email in the members array
            const memberByEmail = profileData.members?.find((member: any) =>
              member.email?.toLowerCase() === user.email!.toLowerCase() && member.isActive === true
            );
            if (memberByEmail) {
              foundMember = memberByEmail;
              foundProfile = { id: profileDoc.id, ...profileData };

              // CRITICAL: Update member's userId to their real Firebase UID
              // Guard: skip if this UID already belongs to another member (e.g., owner)
              // This prevents duplicate userIds when same Google account is used for multiple roles
              const uidAlreadyTaken = user.uid === profileData.ownerId ||
                profileData.members?.some((m: any) =>
                  m.userId === user.uid && m.email?.toLowerCase() !== user.email!.toLowerCase()
                );

              if (memberByEmail.userId !== user.uid && !uidAlreadyTaken) {
                try {
                  const updatedMembers = profileData.members.map((m: any) => {
                    if (m.email?.toLowerCase() === user.email!.toLowerCase()) {
                      return { ...m, userId: user.uid };
                    }
                    return m;
                  });

                  const { syncDenormalizedUserIds } = await import('./profileService');
                  const { memberUserIds, editorUserIds, viewerUserIds, memberEmails } = syncDenormalizedUserIds(updatedMembers);

                  await updateDoc(doc(db, 'profiles', profileDoc.id), {
                    members: updatedMembers,
                    memberUserIds,
                    editorUserIds,
                    viewerUserIds,
                    memberEmails
                  });

                  foundMember = { ...memberByEmail, userId: user.uid };
                } catch (updateError) {
                  console.error('[memberEmails query] Failed to update member userId:', updateError);
                }
              } else if (uidAlreadyTaken) {
                console.warn('[Auth] Skipping userId update — UID already belongs to another member (same Google account used for multiple roles)');
              }
              break;
            }
          }
        } catch (queryError) {
          console.warn('memberEmails query failed:', queryError);
        }
      }

      // If still not found via denormalized arrays, check by email in members array
      // This handles TWO cases:
      // 1. Member exists but profile doesn't have denormalized arrays yet
      // 2. Member's userId in the array is a generated ID, not their Firebase UID (most common case!)
      // We need to find the pilot who has this member - query pilots first then their profiles
      if (!foundMember && !foundInvitation && user.email) {
        // Get all pilots - this is allowed since pilots collection allows read for authenticated users
        const pilotsRef = collection(db, 'pilots');
        const pilotsSnapshot = await getDocs(pilotsRef);

        for (const pilotDocItem of pilotsSnapshot.docs) {
          const pilotId = pilotDocItem.id;

          // Query this pilot's profile - security rules allow reading profiles where user is owner
          // We query by ownerId which the pilot can always read
          try {
            const profileQuery = query(profilesRef, where('ownerId', '==', pilotId));
            const profileSnapshot = await getDocs(profileQuery);

            for (const profileDoc of profileSnapshot.docs) {
              const profileData = profileDoc.data();

              // Check if user's email is in members array (match by EMAIL, not userId!)
              const memberByEmail = profileData.members?.find((member: any) =>
                member.email?.toLowerCase() === user.email!.toLowerCase() && member.isActive === true
              );

              if (memberByEmail) {
                foundMember = memberByEmail;
                foundProfile = { id: profileDoc.id, ...profileData };

                // Also check for accepted invitation
                const invitation = profileData.invitations?.find((inv: any) =>
                  inv.email?.toLowerCase() === user.email!.toLowerCase() &&
                  inv.status === 'accepted'
                );
                if (invitation) {
                  foundInvitation = invitation;
                }

                // CRITICAL: If the member's userId doesn't match Firebase UID, update it!
                // This happens because we generate a random userId when invitation is created,
                // but now the user has signed in with Google and has their real Firebase UID
                // Guard: skip if this UID already belongs to another member (e.g., owner)
                const uidAlreadyTaken = user.uid === profileData.ownerId ||
                  profileData.members?.some((m: any) =>
                    m.userId === user.uid && m.email?.toLowerCase() !== user.email!.toLowerCase()
                  );
                const needsUserIdUpdate = memberByEmail.userId !== user.uid && !uidAlreadyTaken;

                if (uidAlreadyTaken) {
                  console.warn('[Auth] Skipping userId update — UID already belongs to another member (same Google account used for multiple roles)');
                }

                if (needsUserIdUpdate) {
                  try {
                    // Update the member's userId in the members array
                    const updatedMembers = profileData.members.map((m: any) => {
                      if (m.email?.toLowerCase() === user.email!.toLowerCase()) {
                        return { ...m, userId: user.uid };
                      }
                      return m;
                    });

                    // Re-sync denormalized arrays with the new userId
                    const { syncDenormalizedUserIds } = await import('./profileService');
                    const { memberUserIds, editorUserIds, viewerUserIds, memberEmails } = syncDenormalizedUserIds(updatedMembers);

                    await updateDoc(doc(db, 'profiles', profileDoc.id), {
                      members: updatedMembers,
                      memberUserIds,
                      editorUserIds,
                      viewerUserIds,
                      memberEmails
                    });

                    // Update foundMember with the new userId
                    foundMember = { ...memberByEmail, userId: user.uid };
                  } catch (updateError) {
                    console.error('Failed to update member userId:', updateError);
                    // Continue anyway - authentication can still work with email-based lookup
                  }
                } else {
                  // userId matches, but check if denormalized arrays need sync
                  const needsSync = !profileData.memberUserIds?.includes(foundMember.userId) ||
                    (foundMember.role === 'editor' && !profileData.editorUserIds?.includes(foundMember.userId)) ||
                    (foundMember.role === 'viewer' && !profileData.viewerUserIds?.includes(foundMember.userId));

                  if (needsSync) {
                    try {
                      const { syncDenormalizedUserIds } = await import('./profileService');
                      const { memberUserIds, editorUserIds, viewerUserIds, memberEmails } = syncDenormalizedUserIds(profileData.members || []);

                      await updateDoc(doc(db, 'profiles', profileDoc.id), {
                        memberUserIds,
                        editorUserIds,
                        viewerUserIds,
                        memberEmails
                      });
                    } catch (syncError) {
                      console.warn('Failed to sync denormalized arrays:', syncError);
                    }
                  }
                }
                break;
              }

              // Check invitations array for accepted invitation by email
              const invitation = profileData.invitations?.find((inv: any) =>
                inv.email?.toLowerCase() === user.email!.toLowerCase() &&
                inv.status === 'accepted'
              );
              if (invitation) {
                foundInvitation = invitation;
                foundProfile = { id: profileDoc.id, ...profileData };
                break;
              }
            }
          } catch (profileQueryError) {
            // Can't read this pilot's profile - continue to next pilot
            continue;
          }

          if (foundMember || foundInvitation) break;
        }
      }

      // If still not found, check profiles where user is owner (might have been invited to own profile)
      if (!foundMember && !foundInvitation) {
        try {
          const ownerQuery = query(profilesRef, where('ownerId', '==', user.uid));
          const ownerSnapshot = await getDocs(ownerQuery);

          for (const profileDoc of ownerSnapshot.docs) {
            const profileData = profileDoc.data();
            // Check invitations array for accepted invitation by email
            const invitation = profileData.invitations?.find((inv: any) =>
              inv.email?.toLowerCase() === user.email!.toLowerCase() &&
              inv.status === 'accepted'
            );
            if (invitation) {
              foundInvitation = invitation;
              foundProfile = { id: profileDoc.id, ...profileData };
              break;
            }
          }
        } catch (queryError) {
          console.warn('ownerId query failed:', queryError);
        }
      }

      if (!foundMember && !foundInvitation) {
        // User is not a family member on any profile
        // If they're also a Pilot, suggest using the Pilot sign-in
        if (isAlsoPilot) {
          return {
            success: false,
            message: 'You are a Pilot on your own profile. Use the Pilot sign-in option to access your account. If you\'ve also been invited as a family member, check with the Pilot to ensure your invitation is active.'
          };
        }
        // Check if the query failed due to permissions (profile exists but user can't read it)
        // This happens when the pilot's profile doesn't have denormalized arrays yet
        return {
          success: false,
          message: 'Unable to verify your access. Please ask the Pilot (profile owner) to sign in once, then try again.'
        };
      }

      // 3. Check if invitation is expired (only if we found via invitation, not active member)
      // Active members don't need unexpired invitations - they're already verified
      if (!foundMember && foundInvitation && new Date(foundInvitation.expiresAt) < new Date()) {
        return {
          success: false,
          message: 'This invitation has expired. Please ask for a new invitation.'
        };
      }

      // 4. Load/create member profile
      const { ProfileService } = await import('./profileService');
      let memberProfileId: string;
      let memberUserId: string;

      if (foundMember) {
        // Active member - use their existing userId directly
        // They already have a profile created when they first accepted the invitation
        memberUserId = foundMember.userId;

        // Try to find their existing profile by querying Firestore directly
        const memberProfilesRef = collection(db, 'profiles');
        const memberProfileQuery = query(memberProfilesRef, where('ownerId', '==', memberUserId));
        const memberProfileSnapshot = await getDocs(memberProfileQuery);

        if (!memberProfileSnapshot.empty) {
          memberProfileId = memberProfileSnapshot.docs[0].id;
        } else if (foundInvitation) {
          // Fallback: create profile from invitation if it exists
          memberProfileId = await ProfileService.addFamilyMemberFromInvitation(
            foundProfile.id,
            foundInvitation.id
          );
        } else {
          // Create a minimal profile for the active member using ProfileService
          memberProfileId = await ProfileService.createProfileForFamilyMember(
            foundProfile.id,
            foundMember
          );
        }
      } else if (foundInvitation) {
        // Found via invitation (not yet in members array as active)
        memberProfileId = await ProfileService.addFamilyMemberFromInvitation(
          foundProfile.id,
          foundInvitation.id
        );
        const memberProfile = await ProfileService.loadProfile(memberProfileId);
        if (!memberProfile) {
          return {
            success: false,
            message: 'Failed to create member profile. Please try again.'
          };
        }
        memberUserId = memberProfile.ownerId;
      } else {
        // This shouldn't happen, but handle it gracefully
        return {
          success: false,
          message: 'Unable to authenticate. Please try again.'
        };
      }

      // 5. Create session
      const sessionToken = `family_google_${memberUserId}_${Date.now()}`;

      // Use pictureFromToken (from JWT) as primary source, fall back to user.photoURL
      const googleAvatarUrl = pictureFromToken || user.photoURL || '';

      // 6. Upload the Google avatar to Firebase Storage for permanent URL
      // This ensures the avatar is visible to all users without temporary Google URLs
      let avatarUrl = googleAvatarUrl;
      if (googleAvatarUrl && user.email) {
        try {
          // Check if member already has a Firebase Storage avatar
          // If so, skip uploading to avoid duplicate uploads on each login
          const existingAvatar = foundMember?.avatar || '';
          const hasFirebaseStorageAvatar = existingAvatar.includes('firebasestorage');

          if (hasFirebaseStorageAvatar) {
            // Member already has a permanent avatar in Firebase Storage
            avatarUrl = existingAvatar;
          } else {
            // Upload Google photo to Firebase Storage for permanent URL
            const { uploadProfilePictureFromUrl } = await import('./storageService');
            const uploadResult = await uploadProfilePictureFromUrl(googleAvatarUrl, memberUserId);
            avatarUrl = uploadResult.url; // Use the permanent Firebase Storage URL

            // Persist the permanent avatar URL to the member's record
            const { ProfileService: ProfileSvc } = await import('./profileService');
            await ProfileSvc.updateMemberAvatar(user.email, avatarUrl);
          }
        } catch (avatarError) {
          // Non-critical - continue with auth using Google URL as fallback
          console.warn('Failed to upload profile picture to storage, using Google URL:', avatarError);
        }
      }

      // Get display name from member or invitation
      const displayName = foundMember?.displayName || foundInvitation?.displayName || user.displayName || '';
      const lastName = foundMember?.lastName || foundInvitation?.lastName || '';

      return {
        success: true,
        sessionToken,
        userId: memberUserId,
        userType: 'family',
        profile: {
          firstName: displayName,
          lastName: lastName,
          email: user.email,
          avatarUrl  // Include Google profile picture from token or Firebase user
        }
      };
    } catch (error: any) {
      console.error('Family Google auth error:', error);
      return {
        success: false,
        message: error.message || 'Failed to authenticate with Google'
      };
    }
  }

  /**
   * Authenticate Family Member with QR Code + OTP
   */
  async authenticateFamilyQrOtp(_qrCode: string, _otp: string): Promise<FamilyAuthResponse> {
    try {
      // TODO: Implement QR + OTP authentication for family members
      // 1. Verify QR code is valid and not expired
      // 2. Verify OTP matches the one sent to pilot
      // 3. If both valid, create family member session
      // 4. If invalid, return error

      return {
        success: false,
        message: 'Invalid QR Code or OTP. Please try again.'
      };
    } catch (error: any) {
      console.error('Family QR/OTP auth error:', error);
      return {
        success: false,
        message: error.message || 'Invalid QR Code or OTP. Please try again.'
      };
    }
  }

  /**
   * Lightweight check for family memberships (used after Pilot sign-in to detect dual roles).
   * Returns non-owner family memberships where the current user is an active member.
   */
  async checkFamilyMemberships(): Promise<{ profileId: string; ownerName: string; role: string }[]> {
    const user = auth.currentUser;
    if (!user) return [];

    const profilesRef = collection(db, 'profiles');
    const results: { profileId: string; ownerName: string; role: string }[] = [];

    try {
      const memberQuery = query(profilesRef, where('memberUserIds', 'array-contains', user.uid));
      const snapshot = await getDocs(memberQuery);

      for (const profileDoc of snapshot.docs) {
        const data = profileDoc.data();
        // Skip profiles where user is the owner (that's their Pilot account)
        if (data.ownerId === user.uid) continue;

        const member = data.members?.find(
          (m: any) => m.userId === user.uid && m.isActive !== false
        );
        if (member) {
          const ownerMember = data.members?.find((m: any) => m.role === 'owner');
          results.push({
            profileId: profileDoc.id,
            ownerName: ownerMember?.displayName || 'Unknown',
            role: member.role || 'viewer',
          });
        }
      }
    } catch (e) {
      console.warn('[checkFamilyMemberships] query failed:', e);
    }

    return results;
  }

  /**
   * Sign up new Pilot
   */
  async signupPilot(signupData: PilotSignupRequest): Promise<PilotSignupResponse> {
    try {
      // Verify Google token and get user
      // The googleToken from @react-oauth/google is an ID token, so we pass it as the first parameter
      if (!signupData.googleToken) {
        throw new Error('Google token is required for signup');
      }

      const credential = GoogleAuthProvider.credential(signupData.googleToken, null);
      const userCredential = await signInWithCredential(auth, credential);
      const user = userCredential.user;

      // ── Guard: Prevent overwriting existing accounts ──────────────
      // Check 1: Existing pilot doc → block overwrite
      const existingPilotRef = doc(db, 'pilots', user.uid);
      const existingPilot = await getDoc(existingPilotRef);
      if (existingPilot.exists()) {
        return {
          success: false,
          message: 'You already have an account. Please sign in instead.',
        };
      }

      // Check 2: Existing family membership (by UID)
      const profilesRef = collection(db, 'profiles');
      const memberQuery = query(profilesRef, where('memberUserIds', 'array-contains', user.uid));
      const memberSnapshot = await getDocs(memberQuery);
      if (!memberSnapshot.empty) {
        const profileData = memberSnapshot.docs[0].data();
        const ownerMember = profileData.members?.find((m: any) => m.role === 'owner');
        const ownerName = ownerMember?.displayName || 'another user';
        return {
          success: false,
          message: `You already have access to ${ownerName}'s family. Please sign in instead.`,
        };
      }

      // Check 3: Existing family membership (by email — invited but never linked)
      if (user.email) {
        const emailQuery = query(profilesRef, where('memberEmails', 'array-contains', user.email.toLowerCase()));
        const emailSnapshot = await getDocs(emailQuery);
        if (!emailSnapshot.empty) {
          const profileData = emailSnapshot.docs[0].data();
          const ownerMember = profileData.members?.find((m: any) => m.role === 'owner');
          const ownerName = ownerMember?.displayName || 'another user';
          return {
            success: false,
            message: `You've been invited to ${ownerName}'s family. Please sign in instead.`,
          };
        }
      }
      // ──────────────────────────────────────────────────────────────

      // Upload profile picture to Firebase Storage for permanent URL
      // Handles both Google URLs and custom-uploaded data URLs
      let photoURL = '';
      const rawPhotoUrl = signupData.profilePictureUrl || user.photoURL || '';
      if (rawPhotoUrl) {
        try {
          const { uploadProfilePictureFromUrl } = await import('./storageService');
          const uploadResult = await uploadProfilePictureFromUrl(rawPhotoUrl, user.uid);
          photoURL = uploadResult.url;
        } catch (avatarError) {
          // Non-critical — fall back to original URL only if it's a short URL (not base64 data)
          console.warn('Failed to upload profile picture during signup, using original URL:', avatarError);
          photoURL = rawPhotoUrl.startsWith('data:') ? '' : rawPhotoUrl;
        }
      }

      // Determine membership plan from signup selection
      const membershipPlan = signupData.selectedPlan || 'free';
      // ═══════════════════════════════════════════════════════════════
      // v2 PILOT DOCUMENT - Clean architecture
      // Matches: docs/TODO-data-architecture-v2.md
      // ═══════════════════════════════════════════════════════════════
      // Safety: ensure photoURL isn't too large for Firestore (max ~1MB per field)
      const safePhotoURL = photoURL.length > 10000 ? '' : photoURL;

      const pilotData = {
        // Identity (from Google Sign-in)
        firstName: signupData.firstName,
        lastName: signupData.lastName || null,
        email: user.email,
        photoURL: safePhotoURL,
        googleId: user.uid, // Google account ID

        // Subscription
        membershipPlan, // 'free' | 'pro' | 'premium'
        planStartDate: serverTimestamp(),
        // Note: stripeCustomerId is NOT included here - added by Stripe webhook

        // Audit
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        loginCount: 1,
      };

      await setDoc(doc(db, 'pilots', user.uid), pilotData);

      // ═══════════════════════════════════════════════════════════════
      // v2 FAMILY DOCUMENT - Clean architecture
      // Matches: docs/TODO-data-architecture-v2.md
      // ═══════════════════════════════════════════════════════════════
      // Note: serverTimestamp() can't be used inside arrays, so use ISO string
      const now = new Date().toISOString();
      const ownerMember: Record<string, unknown> = {
        userId: user.uid,
        displayName: signupData.firstName,
        email: user.email,
        role: 'owner',
        addedBy: user.uid,
        addedAt: now,
        lastAccessedAt: now,
        isActive: true,
      };
      // CRITICAL FIX: Use 'avatar' property (not 'photoURL') to match Profile type
      // Only set avatar/avatarType if we have a valid photo URL
      if (photoURL) {
        ownerMember.avatar = photoURL;
        ownerMember.avatarType = 'photo';
      }

      await setDoc(doc(db, 'families', user.uid), {
        // Members (denormalized for Firestore queries)
        members: [ownerMember],
        memberUserIds: [user.uid], // For security rules: all members
        editorUserIds: [], // For security rules: Co-Pilots
        viewerUserIds: [], // For security rules: Passengers

        // Invitations (invite link based - no family codes)
        invitations: [],

        // Pre-shared access (for Pro/Drive users)
        preSharedDriveAccess: {
          enabled: false,
          sharedFolderId: null,
          authorizedEmails: [],
        },

        // Audit
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMemberAddedAt: null,
      });

      // Record auth time for deletion risk assessment
      deletionRiskService.recordAuthTime();

      return {
        success: true,
        sessionToken: await user.getIdToken(),
        userId: user.uid,
        // v2: familyCode REMOVED - use invite links
        message: 'Account created successfully'
      };
    } catch (error: any) {
      console.error('Signup error:', error);
      return {
        success: false,
        message: error.message || 'Failed to create pilot account'
      };
    }
  }

  // v2: generateFamilyCode() REMOVED - use invite links instead
  // Family codes have been replaced by invite links in v2

  /**
   * Clear all session data from localStorage.
   * Reusable across all sign-out paths (manual sign-out, forced sign-out, account deletion).
   */
  clearSessionData(): void {
    // Session keys (both old and new names for backwards compatibility)
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userType');
    localStorage.removeItem('lucidDock_sessionToken');
    localStorage.removeItem('lucidDock_userId');
    localStorage.removeItem('lucidDock_userType');
    localStorage.removeItem('lucidDock_familyDriveRootFolderId');
    localStorage.removeItem('lucidDock_familyDriveOwnerEmail');
    localStorage.removeItem('lucidDock_currentProfileId');
    localStorage.removeItem('isAuthenticated');
    // User profile data
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('profilePictureUrl');
    // Cached state
    localStorage.removeItem('lucidDock_cachedPlan');
    localStorage.removeItem('lucidDock_membershipPlan');
    localStorage.removeItem('lucidDock_isAdmin');
    // NOTE: pendingBundleImport is intentionally preserved across auth transitions
    // so share-link imports survive signup/signin flows
    // Per-user data caches (sidebar counts, skeleton loaders)
    localStorage.removeItem('lucidDock_skeletonCounts');
    localStorage.removeItem('lucidDock_skeletonFavorites');
    googleDriveService.disconnect();
    // Session timeout tracking
    sessionTimeoutService.clearActivity();
  }

  /**
   * Sign out user
   */
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth);
      this.clearSessionData();
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback);
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  /**
   * Store session information in localStorage
   * For family members, also stores their profile info (name, email) to override Pilot's Google data
   */
  setSession(
    sessionToken: string,
    userId: string,
    userType: string,
    profileInfo?: { firstName?: string; lastName?: string; email?: string; avatarUrl?: string }
  ): void {
    localStorage.setItem('lucidDock_sessionToken', sessionToken);
    localStorage.setItem('lucidDock_userId', userId);
    localStorage.setItem('lucidDock_userType', userType);
    localStorage.setItem('isAuthenticated', 'true');

    // Initialize session timeout tracking - set initial activity timestamp
    sessionTimeoutService.updateActivity();

    // Clear stale data from previous user before setting new values
    // This prevents avatar cross-contamination and admin privilege leaking between users
    localStorage.removeItem('profilePictureUrl');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    localStorage.removeItem('lucidDock_isAdmin');
    localStorage.removeItem('lucidDock_cachedPlan');
    localStorage.removeItem('lucidDock_membershipPlan');
    localStorage.removeItem('lucidDock_currentProfileId');

    // For family members, store their profile info
    if (userType === 'family' && profileInfo) {
      if (profileInfo.firstName) {
        localStorage.setItem('firstName', profileInfo.firstName);
      }
      if (profileInfo.lastName) {
        localStorage.setItem('lastName', profileInfo.lastName);
      }
      if (profileInfo.email) {
        localStorage.setItem('userEmail', profileInfo.email);
      }
      if (profileInfo.avatarUrl) {
        localStorage.setItem('profilePictureUrl', profileInfo.avatarUrl);
      }
    }
  }

  /**
   * Get session token from localStorage
   */
  getSessionToken(): string | null {
    return localStorage.getItem('lucidDock_sessionToken');
  }

  /**
   * Get user ID from localStorage
   */
  getUserId(): string | null {
    return localStorage.getItem('lucidDock_userId'); // Use ProfileService key
  }

  /**
   * Get user type from localStorage
   */
  getUserType(): string | null {
    return localStorage.getItem('lucidDock_userType');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return localStorage.getItem('isAuthenticated') === 'true' && auth.currentUser !== null;
  }

  /**
   * Clear session information from localStorage
   */
  clearSession(): void {
    // Clear all session keys (both old and new naming conventions)
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userType');
    localStorage.removeItem('lucidDock_sessionToken');
    localStorage.removeItem('lucidDock_userId');
    localStorage.removeItem('lucidDock_userType');
    localStorage.removeItem('lucidDock_familyDriveRootFolderId');
    localStorage.removeItem('lucidDock_familyDriveOwnerEmail');
    localStorage.removeItem('isAuthenticated');
    // Clear user profile data to prevent cross-user data contamination
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('profilePictureUrl');
    // Clear cached plan to prevent stale plan data
    localStorage.removeItem('lucidDock_cachedPlan');
    localStorage.removeItem('lucidDock_isAdmin');
    // NOTE: pendingBundleImport is intentionally preserved across auth transitions
    // so share-link imports survive signup/signin flows
  }

  // ═══════════════════════════════════════════════════════════════
  // v2: verifyMasterPin() REMOVED
  // Google Sign-in is the ONLY auth method in v2
  // ═══════════════════════════════════════════════════════════════

  /**
   * Delete all files from Firebase Storage for a user
   * Files are stored in: users/{userId}/...
   * @param userId - The user ID whose files to delete
   */
  private async deleteUserStorageFiles(userId: string): Promise<void> {
    try {
      const userStorageRef = ref(storage, `users/${userId}`);
      const listResult = await listAll(userStorageRef);

      // Delete all files in root
      const deletePromises: Promise<void>[] = listResult.items.map((itemRef) =>
        deleteObject(itemRef).catch((err) => {
          console.warn(`Failed to delete file ${itemRef.fullPath}:`, err);
        })
      );

      // Recursively delete files in subfolders
      for (const folderRef of listResult.prefixes) {
        const folderList = await listAll(folderRef);
        folderList.items.forEach((itemRef) => {
          deletePromises.push(
            deleteObject(itemRef).catch((err) => {
              console.warn(`Failed to delete file ${itemRef.fullPath}:`, err);
            })
          );
        });

        // Handle nested subfolders (one level deep should be enough for our structure)
        for (const subFolderRef of folderList.prefixes) {
          const subFolderList = await listAll(subFolderRef);
          subFolderList.items.forEach((itemRef) => {
            deletePromises.push(
              deleteObject(itemRef).catch((err) => {
                console.warn(`Failed to delete file ${itemRef.fullPath}:`, err);
              })
            );
          });
        }
      }

      await Promise.all(deletePromises);
    } catch (error) {
      // Storage folder might not exist if user never uploaded files
      console.warn('Failed to delete user storage files (may not exist):', error);
    }
  }

  /**
   * Deactivate Pilot account and optionally delete all associated data
   * This is a destructive action that cannot be undone (when deleteData=true)
   * Note: User must be authenticated (logged in) to call this - no additional PIN verification needed
   * @param userId - The user ID to deactivate
   * @param deleteData - If true, deletes all data. If false (admin testing), only signs out.
   * @param deleteMembers - If true, deletes family members from profile. If false, members preserved.
   */
  async deactivateAccount(userId: string, deleteData: boolean = true, deleteMembers: boolean = true): Promise<{ success: boolean; message: string }> {
    try {
      // Verify the user is a pilot
      const pilotRef = doc(db, 'pilots', userId);
      const pilotDoc = await getDoc(pilotRef);

      // Cancel Stripe subscription immediately (not at period end) on account deletion
      const stripeSubId = pilotDoc.exists() ? pilotDoc.data()?.stripeSubscriptionId : null;
      if (stripeSubId) {
        try {
          // Call API to cancel immediately — different from normal cancel which uses cancel_at_period_end
          await fetch('/api/cancel-subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subscriptionId: stripeSubId, immediate: true }),
          });
        } catch (err) {
          console.warn('Failed to cancel Stripe subscription during account deletion:', err);
        }
      }

      // If admin chooses not to delete data (for testing), just sign out
      if (!deleteData && !deleteMembers) {
        await this.signOut();
        localStorage.clear();
        return { success: true, message: 'Signed out successfully. Data preserved for testing.' };
      }

      // Get the profile ID (usually same as userId for pilots)
      const profilesRef = collection(db, 'profiles');
      const profileQuery = query(profilesRef, where('ownerId', '==', userId));
      const profileSnapshot = await getDocs(profileQuery);

      // Delete Firebase Storage files first (before Firestore cleanup)
      if (deleteData) {
        await this.deleteUserStorageFiles(userId);
      }

      // Delete data based on options
      const deletePromises: Promise<void>[] = [];

      // Only delete data if deleteData is true
      if (deleteData) {
        // 1. Delete all data for this user's profiles (subcollections first, then profile doc)
        for (const profileDoc of profileSnapshot.docs) {
          const profileId = profileDoc.id;

          const healthRef = collection(db, 'profiles', profileId, 'health');
          const healthSnapshot = await getDocs(healthRef);
          healthSnapshot.docs.forEach(healthDoc => {
            deletePromises.push(deleteDoc(doc(db, 'profiles', profileId, 'health', healthDoc.id)));
          });

          const financialRef = collection(db, 'profiles', profileId, 'financial');
          const financialSnapshot = await getDocs(financialRef);
          financialSnapshot.docs.forEach(financialDoc => {
            deletePromises.push(deleteDoc(doc(db, 'profiles', profileId, 'financial', financialDoc.id)));
          });

          const insuranceRef = collection(db, 'profiles', profileId, 'insurance');
          const insuranceSnapshot = await getDocs(insuranceRef);
          insuranceSnapshot.docs.forEach(insuranceDoc => {
            deletePromises.push(deleteDoc(doc(db, 'profiles', profileId, 'insurance', insuranceDoc.id)));
          });

          const bundlesRef = collection(db, 'profiles', profileId, 'shareBundles');
          const bundlesSnapshot = await getDocs(bundlesRef);
          bundlesSnapshot.docs.forEach(bundleDoc => {
            deletePromises.push(deleteDoc(doc(db, 'profiles', profileId, 'shareBundles', bundleDoc.id)));
          });

          deletePromises.push(deleteDoc(doc(db, 'profiles', profileId)));
        }

        // 2. Delete families document
        const familiesRef = doc(db, 'families', userId);
        deletePromises.push(deleteDoc(familiesRef));

        // 3. Delete pilot document
        if (pilotDoc.exists()) {
          deletePromises.push(deleteDoc(pilotRef));
        }
      }

      // Delete members if deleteMembers is true (but not deleteData - members are stored in profile)
      if (deleteMembers && !deleteData) {
        for (const profileDoc of profileSnapshot.docs) {
          deletePromises.push(
            updateDoc(doc(db, 'profiles', profileDoc.id), {
              members: [],
              invitations: [],
              memberUserIds: [],
              editorUserIds: [],
              viewerUserIds: [],
              memberEmails: []
            })
          );
        }
      }

      // Execute core deletions (these are allowed by security rules)
      await Promise.all(deletePromises);

      // Best-effort cleanup for collections with restricted rules
      // These may fail due to security rules — that's OK, orphaned docs are harmless
      if (deleteData) {
        const bestEffort = async (fn: () => Promise<void>) => {
          try { await fn(); } catch { /* security rules may block — ignore */ }
        };

        await bestEffort(async () => {
          const rootBundlesRef = collection(db, 'shareBundles');
          const rootBundlesQuery = query(rootBundlesRef, where('createdBy', '==', userId));
          const rootBundlesSnapshot = await getDocs(rootBundlesQuery);
          await Promise.all(rootBundlesSnapshot.docs.map(d => deleteDoc(doc(db, 'shareBundles', d.id))));
        });

        await bestEffort(async () => {
          const verifyRef = collection(db, 'verificationCodes');
          const verifyQuery = query(verifyRef, where('userId', '==', userId));
          const verifySnapshot = await getDocs(verifyQuery);
          await Promise.all(verifySnapshot.docs.map(d => deleteDoc(doc(db, 'verificationCodes', d.id))));
        });

        await bestEffort(async () => {
          const notifRef = collection(db, 'notification_logs');
          const notifQuery = query(notifRef, where('userId', '==', userId));
          const notifSnapshot = await getDocs(notifQuery);
          await Promise.all(notifSnapshot.docs.map(d => deleteDoc(doc(db, 'notification_logs', d.id))));
        });
      }

      // Sign out the user
      await this.signOut();

      // Clear all local and session storage
      localStorage.clear();
      sessionStorage.clear();

      // Build result message
      let message = 'Signed out successfully.';
      if (deleteData) {
        message = 'Account deleted and all data removed successfully';
      } else if (deleteMembers) {
        message = 'Signed out successfully. Members deleted, data preserved.';
      }

      return { success: true, message };
    } catch (error: any) {
      console.error('Error deactivating account:', error);
      return { success: false, message: error.message || 'Failed to deactivate account' };
    }
  }
}

// Export singleton instance
export const firebaseAuthService = new FirebaseAuthService();

// Export class for testing
export { FirebaseAuthService };
